import { appClient } from '@/lib/appwrite/appClient';
import {
  AppCategory,
  AppRule,
  RuleTriggers,
  RuleActions,
  AppTransaction,
  AppwriteRuleTrigger,
  TriggerType,
  AppwriteRule,
  APP_COLLECTIONS,
} from '@polybank/shared';
import { ID, Query } from 'appwrite';
import { logger } from '@/utils/logger';
import { appConfig } from '@/appConfig';
/**
 * Service for managing rules in Appwrite
 */
export class AppwriteRuleService {
  /**
   * Create a new rule with triggers and actions
   */
  async upsertRule(ruleData: AppRule): Promise<AppRule> {
    try {
      // Create the main rule document

      const rule = await appClient.tables.typed.upsertRow(
        APP_COLLECTIONS.RULES,
        ruleData.$id ?? ID.unique(),
        {
          title: ruleData.title,
          description: ruleData.description || '',
          active: ruleData.active ?? true,
          order: ruleData.order ?? 0,
          strict: ruleData.strict ?? true,
          triggers: ruleData.triggers as RuleTriggers[],
          actions: ruleData.actions as RuleActions[],
        }
      );

      return {
        ...rule,
      };
    } catch (error) {
      logger.error('Failed to create rule:', error);
      throw error;
    }
  }

  /**
   * Create a rule from a transaction (helper for UI)
   */
  createRuleFromTransaction(transaction: AppTransaction): {
    title: string;
    description: string;
    suggestedTriggers: Array<{
      triggerType: AppwriteRuleTrigger['triggerType'];
      triggerValue: string;
      label: string;
    }>;
  } {
    const amount = Math.abs(transaction.amount || 0);
    const formattedAmount = (amount / 100).toFixed(2); // Assuming amount is in cents

    const suggestedTriggers = [
      {
        triggerType: TriggerType.DESCRIPTION_CONTAINS,
        triggerValue:
          (transaction.merchant_name ||
            transaction.description?.split(' ')[0]) ??
          '',
        label: `Description contains "${
          transaction.merchant_name || transaction.description?.split(' ')[0]
        }"`,
      },
      {
        triggerType: TriggerType.AMOUNT_EQUAL,
        triggerValue: formattedAmount,
        label: `Amount equals ${transaction.currency} ${formattedAmount}`,
      },
    ];

    if (transaction.currency) {
      suggestedTriggers.push({
        triggerType: TriggerType.CURRENCY_IS,
        triggerValue: transaction.currency,
        label: `Currency is ${transaction.currency}`,
      });
    }
    if (transaction.gocardless_bank_account_id) {
      suggestedTriggers.push({
        triggerType: TriggerType.ACCOUNT_IS,
        triggerValue: transaction.gocardless_bank_account_id,
        label: 'Same account',
      });
    }
    return {
      title: `Rule for ${transaction.description?.substring(0, 50)}${
        (transaction.description?.length ?? 0 > 50) ? '...' : ''
      }`,
      description: `Auto-generated rule based on transaction from ${
        transaction.transaction_date
          ? new Date(transaction.transaction_date).toLocaleDateString()
          : 'unknown date'
      }`,
      suggestedTriggers,
    };
  }

  /**
   * Get all rules for a user with their triggers and actions
   */
  async getUserRules(): Promise<AppRule[]> {
    try {
      // Get all rules for user
      const rulesResponse = await appClient.tables.typed.listRows(
        APP_COLLECTIONS.RULES,
        [
          Query.orderAsc('order'),
          Query.orderAsc('$createdAt'),
          Query.limit(100),
          Query.select([
            '$id',
            'title',
            'description',
            'active',
            'order',
            'strict',
            'triggers.*',
            'actions.*',
          ]),
        ]
      );

      if (rulesResponse.total === 0) {
        return [];
      }

      // Combine rules with their triggers and actions
      return rulesResponse.rows;
    } catch (error) {
      logger.error('Failed to get user rules:', error);
      throw error;
    }
  }

  /**
   * Get a single rule with its triggers and actions
   */
  async getRule(ruleId: string): Promise<AppRule | null> {
    try {
      const rule = (await appClient.tables.typed.getRow(
        APP_COLLECTIONS.RULES,
        ruleId
      )) as unknown as AppwriteRule;

      // Get triggers for this rule
      const triggersResponse = await appClient.tables.typed.listRows(
        APP_COLLECTIONS.RULE_TRIGGERS,
        [Query.equal('rule_id', ruleId), Query.orderAsc('order')]
      );

      // Get actions for this rule
      const actionsResponse = await appClient.tables.typed.listRows(
        APP_COLLECTIONS.RULE_ACTIONS,
        [Query.equal('rule_id', ruleId), Query.orderAsc('order')]
      );

      return {
        ...rule,
        triggers: triggersResponse.rows,
        actions: actionsResponse.rows,
      };
    } catch (error) {
      logger.error('Failed to get rule:', error);
      return null;
    }
  }

  /**
   * Update a rule
   */
  async updateRule(
    ruleId: string,
    updates: Partial<AppRule>
  ): Promise<AppwriteRule> {
    try {
      const updatedRule = await appClient.tables.typed.updateRow(
        APP_COLLECTIONS.RULES,
        ruleId,
        {
          ...updates,
        }
      );

      return updatedRule as AppwriteRule;
    } catch (error) {
      logger.error('Failed to update rule:', error);
      throw error;
    }
  }

  /**
   * Create a new rule row
   */
  async createRule(data: AppRule): Promise<AppRule> {
    return appClient.tables.typed.createRow(
      APP_COLLECTIONS.RULES,
      ID.unique(),
      data
    );
  }

  /**
   * Batch update rule orders. Only updates rules whose order actually changed.
   */
  async batchUpdateRuleOrder(
    updates: { $id: string; order: number }[]
  ): Promise<void> {
    const tx = await appClient.tables.createTransaction();

    const operations = updates.map((u) => ({
      action: 'update' as const,
      databaseId: appConfig.APPWRITE_DATABASE_ID!,
      tableId: APP_COLLECTIONS.RULES,
      rowId: u.$id,
      data: { order: u.order },
    }));

    if (operations.length > 0) {
      await appClient.tables.createOperations({
        transactionId: tx.$id,
        operations,
      });
    }
    await appClient.tables.updateTransaction({
      transactionId: tx.$id,
      commit: true,
      rollback: false,
    });
  }

  /**
   * Batch move rules to a target group atomically using a transaction.
   */
  async batchMoveRulesToGroup(
    ruleIds: string[],
    groupId: string
  ): Promise<void> {
    if (ruleIds.length === 0) return;

    const tx = await appClient.tables.createTransaction();

    const operations = ruleIds.map((ruleId, index) => ({
      action: 'update' as const,
      databaseId: appConfig.APPWRITE_DATABASE_ID!,
      tableId: APP_COLLECTIONS.RULES,
      rowId: ruleId,
      data: { ruleGroups: groupId, order: index },
    }));

    await appClient.tables.createOperations({
      transactionId: tx.$id,
      operations,
    });

    await appClient.tables.updateTransaction({
      transactionId: tx.$id,
      commit: true,
      rollback: false,
    });
  }

  /**
   * Batch toggle active state for multiple rules atomically.
   */
  async batchToggleRules(
    updates: { $id: string; active: boolean }[]
  ): Promise<void> {
    if (updates.length === 0) return;

    const tx = await appClient.tables.createTransaction();

    const operations = updates.map((u) => ({
      action: 'update' as const,
      databaseId: appConfig.APPWRITE_DATABASE_ID!,
      tableId: APP_COLLECTIONS.RULES,
      rowId: u.$id,
      data: { active: u.active },
    }));

    await appClient.tables.createOperations({
      transactionId: tx.$id,
      operations,
    });

    await appClient.tables.updateTransaction({
      transactionId: tx.$id,
      commit: true,
      rollback: false,
    });
  }

  /**
   * Save a rule with optional new category in a transaction
   */
  async saveRuleWithCategory(
    rule: AppRule,
    newCategory?: AppCategory
  ): Promise<{ rule: AppRule; newCategory?: AppCategory }> {
    const tx = await appClient.tables.createTransaction();

    if (newCategory && !newCategory.is_default) {
      if (!newCategory.$id) {
        newCategory.$id = ID.unique();
      }
      await appClient.tables.typed.upsertRow(
        APP_COLLECTIONS.CATEGORIES,
        newCategory.$id,
        newCategory,
        undefined,
        tx.$id
      );
    }
    const result = await appClient.tables.typed.upsertRow(
      APP_COLLECTIONS.RULES,
      rule.$id ?? ID.unique(),
      rule,
      undefined,
      tx.$id
    );
    await appClient.tables.updateTransaction({
      transactionId: tx.$id,
      commit: true,
      rollback: false,
    });
    return { rule: result, newCategory };
  }
}

// Export singleton instance
export const appwriteRuleService = new AppwriteRuleService();
